import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

import { api } from "@/lib/api";

// Admin view of software requests submitted via /software/:slug.
// Mirrors OrdersPanel's expandable-row pattern so the two are learnable
// together. Status chip is clickable to cycle new → contacted → closed.
const STATUSES = ["new", "contacted", "closed"];
const nextStatus = (s) => STATUSES[(STATUSES.indexOf(s) + 1) % STATUSES.length];

export const SoftwareRequestsPanel = () => {
  const [rows, setRows] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/software-requests");
      setRows(r.data);
    } catch (e) {
      toast.error("Failed to load software requests");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const bump = async (row) => {
    const next = nextStatus(row.status || "new");
    try {
      await api.put(`/admin/software-requests/${row.id}`, { status: next });
      setRows((prev) => prev.map((x) => (x.id === row.id ? { ...x, status: next } : x)));
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  return (
    <div data-testid="admin-software-requests-panel" className="ur-card overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-[#141414] text-[#A3A3A3]">
          <tr>
            <th className="w-10 p-4"></th>
            {["Request", "Software", "Customer", "Email", "Status", "Submitted"].map((h) => (
              <th key={h} className="text-left p-4 font-semibold tracking-wider text-xs uppercase">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td colSpan={7} className="p-10 text-center text-[#A3A3A3]">Loading…</td>
            </tr>
          )}
          {!loading &&
            rows.map((r) => {
              const isOpen = expandedId === r.id;
              return (
                <React.Fragment key={r.id}>
                  <tr
                    className="border-t border-[#1F1F1F] cursor-pointer hover:bg-[#141414]"
                    onClick={() => setExpandedId(isOpen ? null : r.id)}
                    data-testid={`admin-software-req-row-${r.id}`}
                  >
                    <td className="p-4">
                      {isOpen ? (
                        <ChevronDown className="w-4 h-4 text-[#FF6600]" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-[#A3A3A3]" />
                      )}
                    </td>
                    <td className="p-4 font-mono text-xs">{r.id}</td>
                    <td className="p-4">{r.software_name}</td>
                    <td className="p-4">{r.contact?.name}</td>
                    <td className="p-4 text-[#A3A3A3]">{r.contact?.email}</td>
                    <td className="p-4" onClick={(e) => e.stopPropagation()}>
                      <button
                        data-testid={`admin-software-req-status-${r.id}`}
                        onClick={() => bump(r)}
                        className="px-2 py-1 rounded-full text-xs bg-[#1A1A1A] border border-[#262626] hover:border-[#FF6600] transition-colors capitalize"
                        title="Click to cycle status"
                      >
                        {r.status || "new"}
                      </button>
                    </td>
                    <td className="p-4 text-[#A3A3A3] text-xs">
                      {r.created_at?.slice(0, 19).replace("T", " ")}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="border-t border-[#1F1F1F] bg-[#0A0A0A]">
                      <td colSpan={7} className="p-6">
                        <RequestDetails req={r} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          {!loading && rows.length === 0 && (
            <tr>
              <td colSpan={7} className="p-10 text-center text-[#A3A3A3]">
                No software requests yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const RequestDetails = ({ req }) => (
  <div className="grid lg:grid-cols-[1fr_1.4fr] gap-6">
    <div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-[#A3A3A3]">
        Customer
      </div>
      <div className="mt-2 space-y-1 text-sm">
        <div>
          <span className="text-[#A3A3A3]">Name:</span> {req.contact?.name}
        </div>
        <div>
          <span className="text-[#A3A3A3]">Email:</span> {req.contact?.email}
        </div>
        {req.contact?.phone && (
          <div>
            <span className="text-[#A3A3A3]">Phone:</span> {req.contact.phone}
          </div>
        )}
        {req.contact?.business && (
          <div>
            <span className="text-[#A3A3A3]">Business:</span> {req.contact.business}
          </div>
        )}
        {req.contact?.notes && (
          <div className="mt-3">
            <div className="text-[#A3A3A3] text-xs uppercase tracking-widest">Notes</div>
            <div className="mt-1 text-sm whitespace-pre-wrap">{req.contact.notes}</div>
          </div>
        )}
      </div>
    </div>
    <div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-[#A3A3A3]">
        Selections
      </div>
      <div className="mt-2 rounded-lg border border-[#262626] bg-[#141414] p-4">
        <div className="text-sm font-semibold mb-2">
          {req.software_name}{" "}
          <span className="text-[11px] text-[#7A7A7A] font-normal">
            · {req.sales_mode}
          </span>
        </div>
        {(req.selections || []).length === 0 ? (
          <div className="text-xs text-[#7A7A7A] italic">(no options selected)</div>
        ) : (
          <dl className="space-y-1">
            {req.selections.map((s, i) => (
              <div key={i} className="flex gap-3 text-xs">
                <dt className="text-[#7A7A7A] w-40 shrink-0">{s.group_label}</dt>
                <dd className="text-white font-semibold">{s.option_label}</dd>
              </div>
            ))}
          </dl>
        )}
        {req.subtotal > 0 && (
          <div className="mt-3 pt-3 border-t border-[#262626] flex items-center justify-between text-sm">
            <span className="text-[#A3A3A3]">Indicative total</span>
            <span className="font-display font-bold">AWG {req.subtotal}</span>
          </div>
        )}
      </div>
    </div>
  </div>
);
